import { createEffect, createMemo, createSignal, JSX } from 'solid-js';
import { DropDownMenuProps, DropDownMenu } from '@tcweb/connect-ui';
import { SortDirection } from './sort-utils';

const DEFAULT_SORT_DROPDOWN_PROPS: DropDownMenuProps = {
  type: 'icon',
  anchorIcon: 'icon-font tc-icon-sort',
  optionsType: 'both',
  position: 'middle',
  iconTooltip: 'Sort',
  dropDownOptions: [],
};

/**
 * Represents an option in the sort dropdown.
 *
 * Use the `SortableKeyOf` utility type to construct the generic `T`.
 *
 * Example:
 * ```typescript
 * interface Foo {
 *   bar: string;
 *   baz: {
 *     qux: string;
 *   }
 * }
 *
 * type FooSortableKeys = SortableKeyOf<Foo>;
 * // FooSortableKeys = "bar" | "baz.qux"
 *
 * const option: SortDropDownOption<FooSortableKeys> = {
 *   sortBy: "baz.qux", // <-- Type safe!
 *   text: "Baz Qux"
 * };
 * ```
 */
export interface SortDropDownOption<T extends string> {
  /** The property to sort by */
  sortBy: T;
  /** The text to display in the dropdown */
  text: string;
}

export interface SortDropDownSelectProps<T extends string> {
  /** The options to display in the dropdown */
  options: SortDropDownOption<T>[];
  /** The property to sort by */
  sortBy: T;
  /** The direction to sort by */
  sortDirection: SortDirection;
  /** The function to call when the sort changes */
  onSortChanged(sortBy: T, sortDirection: SortDirection): void;
}

/**
 * A sort button with a drop down menu for selecting sort options and direction.
 * @param props
 */
export const SortDropDownSelect = <T extends string>(
  props: SortDropDownSelectProps<T>
): JSX.Element => {
  const [sortOptions, setOptions] = createSignal<SortDropDownOption<T>[]>([]);

  // Get initial signal values from props, using a spread operator to please the linter.
  const { sortBy: initialSortBy, sortDirection: initialSortDirection } = {
    ...props,
  };

  const [sortBy, setSortBy] = createSignal<T>(initialSortBy);
  const [sortDirection, setSortDirection] =
    createSignal<SortDirection>(initialSortDirection);

  const sortDropdownProps = createMemo((): DropDownMenuProps => {
    const dropDownOptions = props.options.map((option) => ({
      text: option.text,
    }));

    return {
      ...DEFAULT_SORT_DROPDOWN_PROPS,
      dropDownOptions,
    };
  });

  const sortDropDownSelectedProps = createMemo(
    (): Pick<DropDownMenuProps, 'selected' | 'customMenuItemIcon'> => {
      const sortOption = sortOptions().find(
        (option) => option.sortBy === sortBy()
      );

      return {
        selected: sortOption?.text,
        customMenuItemIcon:
          sortDirection() === 'asc'
            ? 'tc-icon-arrow-line-up i16'
            : 'tc-icon-arrow-line-down i16',
      };
    }
  );

  const handleSortChanged = (selectedIndex: number): void => {
    const currentSortBy = sortBy();
    const currentSortDirection = sortDirection();

    const selectedSortOption = sortOptions()[selectedIndex];

    const newSortBy = selectedSortOption.sortBy;
    const newSortDirection =
      currentSortBy === newSortBy
        ? currentSortDirection === 'asc'
          ? 'desc'
          : 'asc'
        : currentSortDirection;

    setSortBy(() => newSortBy);
    setSortDirection(newSortDirection);

    props.onSortChanged(newSortBy, newSortDirection);
  };

  createEffect(() => {
    setOptions(props.options);
  });

  createEffect(() => {
    setSortBy(() => props.sortBy);
  });

  createEffect(() => {
    setSortDirection(props.sortDirection);
  });

  return (
    <DropDownMenu
      {...sortDropdownProps()}
      {...sortDropDownSelectedProps()}
      className="m-0" // reset the default right margin
      onSelectionChanged={(_, index) => {
        handleSortChanged(index);
      }}
    />
  );
};
